import { CdkTextareaAutosize } from '@angular/cdk/text-field';
import { formatDate } from '@angular/common';
import { HttpClient, HttpErrorResponse, HttpEventType } from '@angular/common/http';
import { ChangeDetectionStrategy, Component, computed, ElementRef, inject, OnInit, signal, viewChild } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatButton, MatIconButton } from '@angular/material/button';
import { MatCard, MatCardActions, MatCardContent } from '@angular/material/card';
import { MatDialogActions, MatDialogClose, MatDialogContent, MatDialogRef, MatDialogTitle } from '@angular/material/dialog';
import { MatFormField, MatLabel } from '@angular/material/form-field';
import { MatIcon } from '@angular/material/icon';
import { MatInput } from '@angular/material/input';
import { MatProgressBar } from '@angular/material/progress-bar';
import { MatTooltip } from '@angular/material/tooltip';

import { catchError, lastValueFrom, map, of } from 'rxjs';

import { BaasAppState, BaasI18n } from '@lvflanders/bar';
import { BaasRemark, BaasRemarkList, BaasToastMessageService, BarI18nPipe } from '@lvflanders/bar-ng-ui';

import { StartschermV2StateService } from '../../services/v2/startscherm-v2-state.service';
import { MailOnsApiService } from './../../api/services/mail-ons-api.service';

type MailOnsFormModel = {
    name: FormControl<string>;
    phone: FormControl<string>;
    mobilePhone: FormControl<string>;
    email: FormControl<string>;
    alvnummer: FormControl<string>;
    client: FormControl<string>;
    subject: FormControl<string>;
    body: FormControl<string>;
    contact: FormControl<boolean>;
    attachments: FormControl<string>;
    url: FormControl<string>;
    toepassing: FormControl<string>;
};

@Component({
    selector: 'startscherm-mail-ons-dialog',
    templateUrl: './startscherm-mail-ons-dialog.html',
    styleUrls: ['./startscherm-mail-ons-dialog.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        MatDialogTitle,
        MatDialogContent,
        MatLabel,
        FormsModule,
        ReactiveFormsModule,
        MatDialogContent,
        BaasRemarkList,
        BaasRemark,
        MatFormField,
        MatInput,
        CdkTextareaAutosize,
        MatCard,
        MatCardContent,
        MatProgressBar,
        MatTooltip,
        MatIconButton,
        MatIcon,
        MatCardActions,
        MatButton,
        MatDialogActions,
        MatDialogClose,
        BarI18nPipe
    ]
})
export class StartschermMailOnsDialog implements OnInit {
    readonly #dialogRef = inject(MatDialogRef<StartschermMailOnsDialog>);
    readonly #fb = inject(FormBuilder);
    readonly #appState = inject(BaasAppState);
    readonly #stateService = inject(StartschermV2StateService);
    readonly #toastMessage = inject(BaasToastMessageService);
    readonly #translate = inject(BaasI18n);
    readonly #mailOnsApiService = inject(MailOnsApiService);
    readonly #http = inject(HttpClient);

    readonly fileUpload = viewChild<ElementRef>('fileUpload');

    protected readonly files = signal([]);
    protected readonly mailOnsForm: FormGroup<MailOnsFormModel> = this.#fb.group({
        name: [this.#appState.basiscontext.fullUsername, [Validators.required]],
        phone: [''],
        mobilePhone: [''],
        email: [''],
        alvnummer: [this.#appState.basiscontext.klantnummer],
        client: ['', [Validators.maxLength(80)]],
        subject: ['', [Validators.required, Validators.maxLength(255)]],
        body: ['', [Validators.required, Validators.maxLength(1000)]],
        contact: [true],
        attachments: [null],
        url: [this.#appState.location.appRoot.url],
        toepassing: [this.#appState.toepassing.naam]
    });
    protected readonly sendError = signal(false);
    protected readonly inVerzending = signal(false);
    protected readonly time = formatDate(new Date(), 'short', 'nl-BE');
    protected readonly showSubjectInvalidValidation = computed(() => {
        return this.#submitted() && this.#subjectInvalid();
    });
    readonly #submitted = signal(false);
    readonly #subjectInvalid = signal(false);

    async ngOnInit(): Promise<void> {
        const gebruikergegevens = await lastValueFrom(
            this.#mailOnsApiService.getGebruikergegevensByAlvnummer({ alvnummer: this.#appState.basiscontext.klantnummer })
        );

        this.mailOnsForm.controls.phone.patchValue(gebruikergegevens.telefoonnummer);
        this.mailOnsForm.controls.mobilePhone.patchValue(gebruikergegevens.gsmnummer);
        this.mailOnsForm.controls.email.patchValue(gebruikergegevens.emailadres);

        this.#initData();
    }

    protected async onSubmit(): Promise<void> {
        this.#submitted.set(true);
        this.inVerzending.set(true);

        if (!this.mailOnsForm.valid) {
            this.#subjectInvalid.set(this.mailOnsForm.controls.subject.invalid);
            this.inVerzending.set(false);
        } else {
            this.#subjectInvalid.set(false);
            try {
                const res = await lastValueFrom(this.#mailOnsApiService.sendMail({ body: this.mailOnsForm.value }));
                if (res && res.result) {
                    this.#toastMessage.addMessage({
                        text: this.#translate.get('DOMAIN_STARTSCHERM.MAIL_ONS_DIALOG.SEND_SUCCESS'),
                        type: 'success'
                    });
                    this.#dialogRef.close('mail send');
                    this.inVerzending.set(false);
                } else {
                    this.#toastMessage.addMessage({
                        text: this.#translate.get('DOMAIN_STARTSCHERM.MAIL_ONS_DIALOG.SEND_ERROR'),
                        type: 'error'
                    });
                    this.sendError.set(true);
                    this.inVerzending.set(false);
                }
            } catch {
                this.#toastMessage.addMessage({
                    text: this.#translate.get('DOMAIN_STARTSCHERM.MAIL_ONS_DIALOG.SEND_ERROR'),
                    type: 'error'
                });
                this.sendError.set(true);
                this.inVerzending.set(false);
            }
        }
    }

    protected onFileUploadClick(): void {
        const fileUpload = this.fileUpload().nativeElement;
        fileUpload.onchange = () => {
            for (const file of fileUpload.files) {
                this.files.update((files) => [...files, file]);
            }
            this.#uploadFiles();
        };
        fileUpload.click();
    }

    protected deleteFile(fileName): void {
        this.#mailOnsApiService.deleteFile({ file: fileName }).subscribe(() => {
            const updatedFiles = this.files().filter((f) => f.name !== fileName);
            this.files.set(updatedFiles);
        });
    }

    #initData(): void {
        if (this.#stateService.aantalVoorwies > 0) {
            if (this.#stateService.klantnaam) {
                this.mailOnsForm.controls.client.patchValue(this.#stateService.klantnaam.substring(0, 79));
            } else {
                this.mailOnsForm.controls.client.patchValue('');
            }
        }
    }

    #uploadFiles(): void {
        this.fileUpload().nativeElement.value = '';
        this.files().forEach((file) => this.#uploadFile(file));
    }

    #uploadFile(file): void {
        const formData = new FormData();
        formData.append('file', file);
        formData.append('filename', file.name);
        file.inProgress = true;
        file.color = 'primary';
        file.tooltip = file.name;
        this.#http
            .post(`${this.#stateService.rootUrl}/api/extern/upload/upload-file`, formData, {
                reportProgress: true,
                observe: 'events'
            })
            .pipe(
                map((event) => {
                    switch (event.type) {
                        case HttpEventType.UploadProgress:
                            file.progress = Math.round((event.loaded * 100) / event.total);
                            return file;
                        case HttpEventType.Response:
                            return event;
                    }
                }),
                catchError((error: HttpErrorResponse) => {
                    file.inProgress = false;
                    file.color = 'warn';
                    file.tooltip = 'Opladen van het bestand is mislukt';
                    console.log(`${file.data.name} upload failed.`);
                    return of(`${file.data.name} upload failed.`);
                })
            )
            .subscribe((event: any) => {
                if (typeof event === 'object') {
                    this.files.update((files) => {
                        const index = files.findIndex((f) => f.name === file.name);
                        if (index !== -1) {
                            files[index] = file;
                        }
                        return [...files];
                    });
                }
            });
    }
}
